/**
 * File to contain Find a doctor Home page container
 * @function
 */

import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../../utils/context';
import { func, string } from 'prop-types';
import axios from 'axios';
import TableGrid from '../../components/Table.Grid';
import AddNewProvider from '../../components/Card.AddNewProvider';
import Button from 'sharp-component-library/build/Button';
import TextField from 'sharp-component-library/build/TextField';
import SearchIcon from '../../assets/magnifying-glass.svg';
import Heading from 'sharp-component-library/build/Heading';
import Loading from '../../components/Loading';
import Error from '../../components/Error';
import Animation from '../../components/Animation';
import { dataModel } from '../admin/dataMap';
import { useQuery } from '@tanstack/react-query';
import { matchesProviderName } from '../../utils/utilities';

const isNumericProviderSearch = (value) => {
	const trimmedValue = value.trim();
	return trimmedValue !== '' && Number(trimmedValue) > 0;
};

// TODO: see if the search input field is necessary anymore because the table grid serves the same function
function DebouncedSearchInput({
	value: initialValue,
	onChange,
	onInputChange,
	debounce = 500,
	...props
}) {
	const [value, setValue] = useState(initialValue);

	useEffect(() => {
		setValue(initialValue);
	}, [initialValue]);

	useEffect(() => {
		const timeout = setTimeout(() => {
			onChange(value);
		}, debounce);

		return () => clearTimeout(timeout);
	}, [value]);

	return (
		<TextField
			{...props}
			hideLabel='true'
			value={value}
			onChange={(e) => {
				const nextValue = e.target.value;
				setValue(nextValue);
				onInputChange?.(nextValue);
				if (nextValue.trim() === '') {
					onChange('');
				}
			}}
		/>
	);
}

DebouncedSearchInput.propTypes = {
	onInputChange: func,
};

const Home = ({ componentName }) => {
	const { errorRef, errorAnimate, error, setError, setToggleBasicInformation } =
		useContext(AppContext);

	const navigate = useNavigate();

	const resource = 'providers';
	const { title: gridTitle, map: cols, key: resourceKey } = dataModel[resource];
	const [query, setQuery] = useState('');
	const [searchInputValue, setSearchInputValue] = useState('');
	const [addNewProvider, setAddNewProvider] = useState(false);
	const [nameFirstValue, setNameFirstValue] = useState('');
	const [nameLastValue, setNameLastValue] = useState('');
	const [nameFirstError, setNameFirstError] = useState(false);
	const [nameLastError, setNameLastError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [addNewLoading, setAddNewLoading] = useState(false);
	const [errorMsg, setErrorMsg] = useState('');

	const callApi = async (queryFilter) => {
		setError('');
		setLoading(true);
		try {
			const { data } = await axios.get(
				`providers?${queryFilter}`
			);
			setLoading(false);
			return data[resourceKey];
		} catch (error) {
			console.error('getProviders error => ', error);
			setLoading(false);
		}
	};

	const filterProvider = (value) => {
		const q = value.toLowerCase().trim();
		return callApi(`filter[cactus_id][eq]=${q}`);
	};
	
	const { data: allProviderData, isLoading, refetch: refetchAllProviderData } = useQuery({
		queryKey: [resource],
		queryFn: () => axios.get(`/providers?filter[name_formatted][ilike]=`).then(res => res.data[resourceKey]),
		staleTime: Infinity,
		cacheTime: 10 * 60 * 1000, // 10 minutes
	});

	const isNumericSearch = isNumericProviderSearch(query);

	const { data: searchedProviderData } = useQuery({
		queryKey: ['providers-search', query],
		queryFn: () => filterProvider(query),
		staleTime: Infinity,
		cacheTime: 10 * 60 * 1000, // 10 minutes
		enabled: isNumericSearch,
	});

	const filteredProviderData = query.trim().length > 0 && !isNumericSearch
		? (allProviderData ?? []).filter((provider) =>
			matchesProviderName(provider.name_formatted, query)
		)
		: allProviderData;

	// submit function to save changes
	let tableData = allProviderData;

	if (query.trim().length > 0) {
		if (isNumericSearch) {
			tableData = searchedProviderData;
		} else {
			tableData = filteredProviderData;
		}
	}

	const handleSubmitNewProvider = async (payload) => {
		let result = { successful: false };
		setAddNewLoading(true);
		if (payload) {
			try {
				const { data } = await axios.post('/providers', payload);
				setError(false);
				result = { ...data, successful: true };
				await refetchAllProviderData();
			} catch (error) {
				if (error.response.data.errors) {
					let msg = '';
					error.response.data.errors.forEach((err) => {
						msg += err.path + ' ' + err.message + '\n';
					});
					setErrorMsg(msg);
				}
				setError(true);
			}
			setAddNewLoading(false);
			return result;
		}
	};

	const resetAddNewProvider = () => {
		setNameFirstValue('');
		setNameFirstError(false);
		setNameLastValue('');
		setNameLastError(false);
		setAddNewProvider(false);
	};

	const handleClose = () => {
		errorAnimate();
		setTimeout(() => {
			setError(false);
		}, 450);
	};

	return (
		<div
			className={`${componentName} container datagrid-home`}
			data-testid={componentName}
		>
			{!addNewProvider && (
				<div className='home-top'>
					<div className='search' data-testid='provider-search'>
						<Heading level={2}>Providers</Heading>
						<div className='search-bar'>
							<DebouncedSearchInput
								value={query}
								onInputChange={setSearchInputValue}
								onChange={(value) => setQuery(value)}
								type='text'
								placeholder='Search for a provider by name or physician ID'
								data-testid='global-filter-input'
							/>
							<Button onClick={() => setQuery(searchInputValue.trim())}>
								<img src={SearchIcon} alt='magnifying icon' />
							</Button>
						</div>
					</div>
					<Button
						className={'add-new-provider-btn'}
						data-testid='add-new-provider-btn'
						onClick={() => {
							setAddNewProvider(true);
						}}
					>
						+ Add new provider
					</Button>
				</div>
			)}

			{!!addNewProvider && (
				<AddNewProvider
					nameFirstOnChange={(event) => {
						setNameFirstValue(event.target.value);
						setNameFirstError(event.target.value.trim() === '');
					}}
					nameFirstValue={nameFirstValue}
					nameFirstError={nameFirstError}
					nameFirstErrorText={'This field is required.'}
					// last name
					nameLastOnChange={(event) => {
						setNameLastValue(event.target.value);
						setNameLastError(event.target.value.trim() === '');
					}}
					nameLastValue={nameLastValue}
					nameLastError={nameLastError}
					nameLastErrorText={'This field is required.'}
					submitCancel={() => resetAddNewProvider()}
					loading={addNewLoading}
					error={!!error}
					errorMsg={errorMsg}
					errorRef={errorRef}
					// handle save and cancel
					submitSave={async () => {
						if (nameFirstValue.trim() === '' || nameLastValue.trim() === '') {
							setNameFirstError(nameFirstValue.trim() === '');
							setNameLastError(nameLastValue.trim() === '');
							return;
						}

						const payload = {
							name_first: nameFirstValue,
							name_last: nameLastValue,
						};

						const addNewSubmit = await handleSubmitNewProvider(payload);
						if (addNewSubmit.successful) {
							setToggleBasicInformation(true);
							navigate(`${addNewSubmit.id}`);
						}
					}}
					handleClose={() => resetAddNewProvider()}
				/>
			)}

			{!!error && (
				<Error
					errorMsg='There was an error retrieving the provider data. Please try again.'
					handleClose={handleClose}
					errorRef={errorRef}
				/>
			)}
			{(!!isLoading || !!loading)&& <Loading />}

			{(!isLoading && !loading) && (
				<Animation typeOfAnimation='fade-in'>
					<TableGrid
						title={gridTitle}
						data={tableData ?? []}
						cols={cols}
						sortColumnId={'name_formatted'}
						filterColumns={true}
					/>
				</Animation>
			)}
		</div>
	);
};

Home.propTypes = {
	/**
	 * Define component name
	 */
	componentName: string,
};

Home.defaultProps = {
	componentName: 'home',
};

export default Home;
